// Keep this order for the two first imports
// 1. web-setup
// 2. i18n
import '@/web-setup';

import { IS_DESKTOP, IS_NATIVE } from '@/config/platform';
import '@/i18n';
import { captureAcquisition, captureReferralCode } from '@/utils/referral';

// Marqueur posé avant le premier rendu : c'est lui qui déduit la barre de titre
// d'Electron de `--app-height` (global.css). En CSS et non en JS, sinon chaque
// conteneur plein écran refait le calcul dans sa classe.
if (IS_DESKTOP) document.documentElement.classList.add('is-electron');

import * as Sentry from '@sentry/react';

const environment =
  import.meta.env.VITE_NODE_ENV ??
  // Sous Capacitor l'hôte vaut toujours `localhost` : seul le mode du build y
  // distingue le développement de la production.
  ((IS_NATIVE ? import.meta.env.DEV : window.location.hostname === 'localhost')
    ? 'development'
    : 'production');

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  environment,
  release: window.api.getVersion(),
});

import '@/assets/global.css';
import TanstackProvider from '@/components/providers/tanstack-provider';
import muiTheme from '@/utils/mui-theme';
import { ThemeProvider } from '@mui/material';
import {
  RouterProvider,
  createBrowserHistory,
  createHashHistory,
  createRouter,
} from '@tanstack/react-router';
import { PropsWithChildren, StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { PostHogProvider } from 'posthog-js/react';
import { routeTree } from './routeTree.gen';
// Import the generated route tree
import { SnackbarProvider } from '@/components/snackbar';
import { bootstrapDemoSession } from '@/utils/demo-mode';
import { setupNative } from '@/native-setup';
import { CaptureResult } from 'posthog-js';

// Create a new router instance
const router = createRouter({
  routeTree,
  // Une WebView native sert ses fichiers depuis un schéma local : comme
  // Electron, elle n'a pas d'historique d'URL serveur à suivre.
  history: IS_DESKTOP || IS_NATIVE ? createHashHistory() : createBrowserHistory(),
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function PostHogConditionalProvider({ children }: PropsWithChildren) {
  if (environment === 'development') return children;
  return (
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        defaults: '2025-11-30',
        debug: environment === 'development',
        before_send: (event: CaptureResult | null): CaptureResult | null => {
          // Use hash for electron
          if ((IS_DESKTOP || IS_NATIVE) && event?.properties?.$current_url) {
            const parsed = new URL(event.properties.$current_url);
            if (parsed.hash) {
              event.properties.$pathname = parsed.hash.slice(1);
              event.properties.$current_url = `https://app.kara-trading-tools.com${parsed.hash.slice(1)}`;
              event.properties.$host = 'app.kara-trading-tools.com';
            }
          }
          return event;
        },
        capture_performance: window.store.get('admin_override_user_id')
          ? false
          : true,
        capture_pageleave: window.store.get('admin_override_user_id')
          ? false
          : true,
        capture_pageview: window.store.get('admin_override_user_id')
          ? false
          : true,
        autocapture: false,
        // Enregistrer l'écran demande un accord explicite (règle 2.5.14), et
        // la fiche de confidentialité de l'App Store ne le déclare pas. Les
        // enquêtes téléchargent du code à la volée (règle 2.5.2).
        disable_session_recording: IS_NATIVE,
        disable_surveys: IS_NATIVE,
      }}
    >
      {children}
    </PostHogProvider>
  );
}

// Render the app
const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  // Deux choses doivent être prêtes avant le premier rendu. Le stockage natif,
  // parce que `window.store` est lu synchroniquement dès le chargement des
  // modules et qu'il faut donc l'avoir hydraté. Et la session de démonstration,
  // sans quoi la route racine redirige vers /auth le temps que le jeton arrive.
  void setupNative()
    .then(() => {
      // Les applications mobiles ne lisent ni parrain ni provenance.
      if (!IS_NATIVE) {
        captureReferralCode();
        captureAcquisition();
      }
      return bootstrapDemoSession();
    })
    .finally(() =>
    root.render(
      <StrictMode>
        <PostHogConditionalProvider>
          <TanstackProvider>
            <ThemeProvider theme={muiTheme}>
              <SnackbarProvider>
                <RouterProvider router={router} />
              </SnackbarProvider>
            </ThemeProvider>
          </TanstackProvider>
        </PostHogConditionalProvider>
      </StrictMode>,
    ),
  );
}
